// Import Swiper styles
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import { Box, Text, Image, Tag } from 'components/elements'
import { Pagination, Navigation, Thumbs } from 'swiper/modules'
import { CSS, styled } from 'theme'
import { Close } from 'components/icons'
import { ButtonTransparent, overflowText } from 'theme/globalStyleCss'

type Breakpoints = {
  [width: number]: {
    slidesPerView?: number
    spaceBetween?: number
  }
}

type SettingProps = {
  className?: string
  onSwiper?: (swiper: any) => void
  slidesPerView: number | undefined
  spaceBetween: number
  navigation?: boolean | undefined
  thumbs?: {
    swiper: SwiperClass | null
  }
  modules: any[]
  freeMode?: boolean
  watchSlidesProgress?: boolean
  on: {
    slideChange: () => void
    progress: (s: object, progress: number) => void
  }
  pagination?: {
    el: string
    clickable: boolean
  }
  breakpoints?: Breakpoints
}

export type MainSliderProps = {
  url: string
  title: string
  description: string
}

type SliderProps = {
  css?: CSS
  overrideCss?: CSS
  navSliderCss?: CSS
  selectedImageIndex: number | null
  onSelectedImageChange: (index: number | null) => void
  showHideSlider?: boolean
  carouselItems: MainSliderProps[]
  sliderProps: {
    slideCount?: number
    pagination?: boolean
    navigation?: boolean
  }
  sliderNavProps?: {
    subNavItems: MainSliderProps[]
    slideCount?: number
    slideCountMobile?: number
    pagination?: boolean
    navigation?: boolean
    isCentered: boolean
  }
}

const SwiperSlider = ({
  css,
  overrideCss,
  navSliderCss,
  showHideSlider,
  carouselItems,
  sliderProps,
  sliderNavProps,
  selectedImageIndex,
  onSelectedImageChange,
}: SliderProps) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null)
  const [showSelectedItem, setShowSelectedItem] = useState<boolean>(
    !!showHideSlider
  )

  const swiperRef = useRef<SwiperClass | null>(null)

  const sliderPaginationConfig = sliderProps?.pagination
    ? {
        el: '.swiper-pagination',
        clickable: true,
      }
    : undefined

  const sliderNavPaginationConfig = sliderNavProps?.pagination
    ? {
        el: '.swiper-pagination',
        clickable: true,
      }
    : undefined

  const settings: SettingProps = {
    className: 'mainSwiper',
    onSwiper: swiper => {
      swiperRef.current = swiper
    },
    slidesPerView: sliderProps?.slideCount,
    spaceBetween: 30,
    navigation: sliderProps?.navigation || undefined,
    thumbs: {
      swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
    },
    modules: [Navigation, Thumbs, Pagination],
    on: {
      slideChange: () => console.log('slide changed'),
      progress: (s: object, progress: number) => {},
    },
    // breakpoints: { 768: { slidesPerView: 4 } },
  }

  const navSettings: SettingProps = {
    className: 'navSwiper',
    onSwiper: swiper => {
      setThumbsSwiper(swiper)
    },
    slidesPerView: sliderNavProps?.slideCountMobile,
    spaceBetween: 10,
    // navigation: sliderNavProps?.navigation ? {
    //   nextEl: '.swiper-button-next',
    //   prevEl: '.swiper-button-prev'
    // } : undefined,
    navigation: sliderNavProps?.navigation || undefined,
    modules: [Navigation, Thumbs, Pagination],
    freeMode: true,
    watchSlidesProgress: true,
    on: {
      slideChange: () => console.log('slide changed'),
      progress: (s: object, progress: number) => {},
    },
    //above 600px
    breakpoints: {
      600: {
        slidesPerView: sliderNavProps?.slideCount,
      },
    },
  }

  if (sliderPaginationConfig) {
    settings.pagination = sliderPaginationConfig
  }

  if (sliderNavPaginationConfig) {
    navSettings.pagination = sliderNavPaginationConfig
  }

  const SwiperSlideCss = {
    '&.mainSwiper': {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      zIndex: 101,
      display: showSelectedItem ? 'block' : 'none',
      background: '$gray50',
      // borderRadius: '$radii$sm',
    },
  }

  useEffect(() => {
    if (selectedImageIndex !== null && swiperRef.current) {
      swiperRef.current.slideTo(selectedImageIndex)
      setShowSelectedItem(true)
    }
  }, [selectedImageIndex])

  const handleClose = useCallback(() => {
    setShowSelectedItem(false)
    onSelectedImageChange(null)
  }, [onSelectedImageChange])

  const swiperWrapperCustomStyles = {
    '& .swiper-wrapper': {
      justifyContent: 'center',
    },
  }

  const responsiveSwiperStyles = {
    ...(carouselItems.length < (sliderNavProps?.slideCountMobile ?? 3) &&
      swiperWrapperCustomStyles),
    '@media (min-width: 600px)': {
      ...(carouselItems.length < (sliderNavProps?.slideCount ?? 5) &&
        swiperWrapperCustomStyles),
    },
  }

  return (
    <>
      {!!showSelectedItem && (
        <ButtonTransparent
          className="transparent"
          size="sm"
          type="button"
          iconButton="gray"
          css={{
            ...CloseSlideCss,
            ...(overrideCss &&
              Object.keys(overrideCss).length > 0 && { display: 'none' }),
          }}
          onClick={() => {
            setShowSelectedItem(false)
            handleClose()
          }}
          children={<Close width={20} height={20} />}
        />
      )}
      <SwiperWrap
        css={{
          ...css,
          ...SwiperSlideCss,
          ...overrideCss,
        }}
        {...settings}
      >
        {carouselItems?.map((item, index) => (
          <SwiperSlide key={index}>
            <Image size="full" url={item.url} />
            {(item.title || item.description) && (
              <Box
                css={{
                  position: 'absolute',
                  top: '$space$15',
                  left: '$space$15',
                  maxWidth: 150,
                }}
              >
                <Tag
                  size="rounded"
                  color="gray"
                  children={item.title}
                  css={{ display: 'block', ...overflowText }}
                />
                <Text as="h5" css={{ color: '$white25' }}>
                  {item.description}
                </Text>
              </Box>
            )}
          </SwiperSlide>
        ))}
      </SwiperWrap>
      {!!sliderNavProps?.subNavItems?.length && (
        <SwiperWrap
          css={{
            ...navSliderCss,
            '&.navSwiper': {
              'mt': '-$space$5',
              'p': '$space$10',
              'pt': '$space$15',
              'mb': '$space$25',
              'border': '1px solid $border',
              'borderTop': 0,
              // 'borderBottomRightRadius': '$radii$md',
              // 'borderBottomLeftRadius': '$radii$md',
              'background': '$whiteBase',
              '.swiper-slide': {
                display: 'flex',
                img: {
                  // borderRadius: '$radii$md',
                  aspectRatio: '4 / 3',
                  objectFit: 'cover',
                },
              },
            },
            ...responsiveSwiperStyles,
          }}
          {...navSettings}
        >
          {carouselItems?.map((item, index) => (
            <SwiperSlide key={index} onClick={() => setShowSelectedItem(true)}>
              {/* <SwiperSlide key={index}> */}
              <Image size="full" url={item.url} />
              {(item.title || item.description) && (
                <Box
                  css={{
                    position: 'absolute',
                    top: '$space$8',
                    left: '$space$8',
                    zIndex: 1,
                    maxWidth: 'calc(100% - $space$16)',
                  }}
                >
                  <Tag
                    size="roundedSmall"
                    color="gray"
                    children={item.title}
                    css={{ display: 'block', ...overflowText }}
                  />
                  <Text as="h5" css={{ color: '$white25' }}>
                    {item.description}
                  </Text>
                </Box>
              )}
            </SwiperSlide>
          ))}
        </SwiperWrap>
      )}
    </>
  )
}

export default SwiperSlider

export const SwiperWrap = styled(Swiper, {
  '.swiper-slide': {
    'display': 'flex',
    'cursor': 'pointer',
    '&-thumb-active:after': {
      content: '',
      position: 'absolute',
      left: 0,
      top: 0,
      display: 'block',
      width: '100%',
      height: '100%',
      background:
        'linear-gradient(rgba(0, 0, 0, 0.1) 0, rgba(0, 0, 0, 0.5) 80%);',
      // borderRadius: '$radii$md',
    },
  },
})
export const SwiperSlideWrap = styled(SwiperSlide, {})

const CloseSlideCss = {
  position: 'absolute',
  top: '$space$15',
  right: '$space$15',
  zIndex: 102,
  borderRadius: '$radii$rounded',
}
